/**
 * Country Landing Pages — Wave 4
 * 11 new BPO countries across 4 regions:
 * Latin America: Peru, Chile
 * Sub-Saharan Africa: Ghana, Senegal, South Africa
 * Southeast Asia: Indonesia, Singapore
 * Eastern Europe: Romania, Bulgaria, Czech Republic, Hungary
 */
import type { CountryLandingPageData } from '@/components/templates/CountryLandingPageTemplate';

const sharedKPIs = [
  { value: '>95%', label: 'CSAT Target' },
  { value: '>85%', label: 'FCR Rate' },
  { value: '<20s', label: 'Response Time' },
  { value: '99.9%', label: 'Uptime SLA' },
];

const sharedTimeline = [
  { week: 'W1', milestone: 'Discovery call, requirements analysis, and team specification' },
  { week: 'W2', milestone: 'Agent recruitment, screening, and selection' },
  { week: 'W3', milestone: 'Training, systems integration, and quality calibration' },
  { week: 'W4', milestone: 'Pilot launch with live customer interactions and QA monitoring' },
];

const sharedServices = [
  { name: 'Inbound Customer Support', description: '24/7 phone, chat, and email support with native-speaking agents trained in your brand voice and processes.' },
  { name: 'Outbound Sales & Telemarketing', description: 'B2B and B2C telesales, appointment setting, lead qualification, and follow-up campaigns.' },
  { name: 'Technical Support & Helpdesk', description: 'Tier 1-3 technical assistance for software, hardware, and SaaS products with documented escalation paths.' },
  { name: 'Back Office Processing', description: 'Data entry, invoice processing, order management, and document verification with 99.5%+ accuracy.' },
  { name: 'Chat & Email Support', description: 'Omnichannel digital support via live chat, WhatsApp, social media, and email with <1h response time.' },
  { name: 'Customer Retention & Winback', description: 'Proactive outreach programs to reduce churn, recover at-risk accounts, and increase customer lifetime value.' },
];

const sharedRelated = [
  { label: 'Why Morocco for BPO?', href: '/en/why-morocco' },
  { label: 'BPO Services Overview', href: '/en/services/bpo' },
  { label: 'Pricing Models', href: '/en/pricing' },
  { label: 'Case Studies', href: '/en/case-studies' },
  { label: 'Contact Us', href: '/en/contact' },
  { label: 'Industries We Serve', href: '/en/industries' },
];

function makeCountry(overrides: Partial<CountryLandingPageData> & Pick<CountryLandingPageData, 'slug' | 'countryName' | 'countryFlag' | 'timezone' | 'languages' | 'currency' | 'title' | 'description' | 'keywords' | 'heroTitle' | 'heroSubtitle' | 'whyTitle' | 'whyIntro' | 'whyReasons' | 'advantages' | 'pricingComparison' | 'industries' | 'faqs'>): CountryLandingPageData {
  return {
    heroStats: [
      { value: '50-70%', label: 'Cost Savings' },
      { value: '2-4 wks', label: 'Go-Live Time' },
      { value: '12+', label: 'Languages' },
      { value: '24/7', label: 'Coverage' },
    ],
    services: sharedServices,
    kpis: sharedKPIs,
    timelineSteps: sharedTimeline,
    relatedLinks: sharedRelated,
    ...overrides,
  };
}

export const COUNTRY_PAGES_WAVE4: Record<string, CountryLandingPageData> = {

  // ═══════════════════════════════════════════════════════════
  // LATIN AMERICA
  // ═══════════════════════════════════════════════════════════

  peru: makeCountry({
    slug: 'call-center-for-peru',
    countryName: 'Peru',
    countryFlag: '🇵🇪',
    timezone: 'PET (GMT-5)',
    languages: ['Spanish (native)', 'English', 'French', 'Arabic'],
    currency: 'PEN / USD',
    title: 'Call Center Peru 2026 | BPO Outsourcing Perú – Call IT Dev',
    description: 'Call center outsourcing for Peru. Native Spanish agents, LATAM coverage, European multilingual support. 60% savings. Free pilot!',
    keywords: ['call center Peru', 'BPO Peru', 'call center outsourcing Peru', 'centro de llamadas Perú', 'externalización Perú', 'customer support Peru'],
    heroTitle: 'Call Center for Peru — Multilingual BPO from Morocco',
    heroSubtitle: 'Peruvian companies: native Spanish agents + European language coverage (French, German, Italian). 60% savings vs Lima. GDPR-compliant, ISO 27001.',
    whyTitle: 'Why Peruvian Companies Choose Call IT Dev',
    whyIntro: 'Peru\'s growing economy needs scalable customer support that covers both LATAM and European markets. Morocco delivers native Spanish plus 12+ additional languages.',
    whyReasons: [
      { icon: '🗣️', title: 'Native Spanish Agents', description: 'Fluent Spanish speakers for seamless Peruvian customer interactions.' },
      { icon: '🌍', title: 'European Languages', description: 'French, German, Italian, Portuguese — expand to EU markets effortlessly.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Significantly lower than Lima-based operations with higher multilingual capacity.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'ISO 27001 certified — essential for international expansion.' },
      { icon: '⏰', title: 'Extended Hours', description: 'GMT+1 complements PET (GMT-5) for near 24/7 coverage.' },
      { icon: '📈', title: 'Scalable Teams', description: 'From 5 to 500 agents. Scale with Peru\'s growing market.' },
    ],
    advantages: [
      { title: 'LATAM + EU Coverage', description: 'Serve both Latin American and European customers from one center.' },
      { title: 'Mining & Extractives Expertise', description: 'Support for Peru\'s key industries including mining, agriculture, and textiles.' },
      { title: 'Omnichannel', description: 'Phone, chat, email, WhatsApp, social media support.' },
      { title: 'Quality Monitoring', description: 'Real-time QA, call recording, CSAT tracking.' },
      { title: 'Cost Efficiency', description: '60% savings vs onshore with superior multilingual capabilities.' },
      { title: 'Fast Deployment', description: '2-4 week go-live for any language combination.' },
    ],
    pricingComparison: { local: '$8-15', morocco: '$6-12', savings: '30-50%' },
    industries: [
      { name: 'Mining & Resources', description: 'Customer and supplier support for Peru\'s mining sector.' },
      { name: 'E-commerce', description: 'Multilingual customer support for online retailers.' },
      { name: 'Banking & Finance', description: 'Customer service for Peruvian financial institutions.' },
      { name: 'Telecom', description: 'Technical and billing support for telecom operators.' },
      { name: 'Tourism', description: 'Multilingual booking and concierge services.' },
      { name: 'Agriculture & Export', description: 'B2B support for Peru\'s agricultural export sector.' },
    ],
    faqs: [
      { question: 'Do your agents speak Peruvian Spanish?', answer: 'Our Spanish-speaking agents are trained in neutral Latin American Spanish that resonates with Peruvian customers. We adapt vocabulary and tone to match your brand.' },
      { question: 'What is the timezone difference?', answer: 'Morocco (GMT+1) is 6 hours ahead of Peru (GMT-5). This enables extended coverage and near 24/7 operations when combined with your local team.' },
    ],
  }),

  chile: makeCountry({
    slug: 'call-center-for-chile',
    countryName: 'Chile',
    countryFlag: '🇨🇱',
    timezone: 'CLT (GMT-4)',
    languages: ['Spanish (native)', 'English', 'French', 'Portuguese'],
    currency: 'CLP / USD',
    title: 'Call Center Chile 2026 | BPO Outsourcing Chile – Call IT Dev',
    description: 'Call center outsourcing for Chile. Native Spanish agents, European multilingual support, GDPR-compliant. 55% savings vs Santiago.',
    keywords: ['call center Chile', 'BPO Chile', 'centro de llamadas Chile', 'externalización Chile', 'customer support Chile', 'outsourcing Santiago'],
    heroTitle: 'Call Center for Chile — Premium BPO from Morocco',
    heroSubtitle: 'Chilean companies: native Spanish + European languages. 55% savings vs Santiago agencies. ISO 27001, GDPR-compliant.',
    whyTitle: 'Why Chilean Companies Choose Call IT Dev',
    whyIntro: 'Chile\'s sophisticated market demands high-quality, multilingual customer support. Morocco offers the perfect nearshore solution for LATAM and European expansion.',
    whyReasons: [
      { icon: '🗣️', title: 'Native Spanish', description: 'Fluent Spanish agents trained for Chilean market expectations.' },
      { icon: '🌍', title: 'EU Market Access', description: 'French, German, Italian for Chile\'s European trade partners.' },
      { icon: '💰', title: '55% Savings', description: 'Lower costs than Santiago with superior multilingual capacity.' },
      { icon: '🛡️', title: 'Compliance', description: 'ISO 27001, GDPR — matching Chile\'s high data protection standards.' },
      { icon: '📊', title: 'Analytics', description: 'Real-time dashboards, CSAT, NPS, FCR tracking.' },
      { icon: '🚀', title: 'Fast Launch', description: '2-3 weeks from contract to live operations.' },
    ],
    advantages: [
      { title: 'OECD-Grade Quality', description: 'Chile\'s OECD standards met with ISO-certified operations.' },
      { title: 'Mining Sector Support', description: 'B2B support for Chile\'s copper and lithium industries.' },
      { title: 'FinTech Ready', description: 'Support for Chile\'s growing fintech ecosystem.' },
      { title: 'Scalable', description: 'From startup to enterprise — flexible team sizes.' },
      { title: 'Omnichannel', description: 'Phone, chat, email, social media, WhatsApp.' },
      { title: 'Quality Assurance', description: 'Dedicated QA team with call monitoring and coaching.' },
    ],
    pricingComparison: { local: '$10-18', morocco: '$6-12', savings: '55%+' },
    industries: [
      { name: 'Mining & Lithium', description: 'B2B support for Chile\'s mining and lithium sector.' },
      { name: 'FinTech', description: 'Customer support for Chile\'s fintech companies.' },
      { name: 'Wine & Agriculture', description: 'Export support for Chile\'s agricultural sector.' },
      { name: 'Telecom', description: 'Customer service for Chilean telecom providers.' },
      { name: 'E-commerce', description: 'Multilingual support for online retailers.' },
      { name: 'Solar & Renewable Energy', description: 'Support for Chile\'s clean energy sector.' },
    ],
    faqs: [
      { question: 'Can you handle Chilean Spanish?', answer: 'Yes. Our agents are trained in Chilean Spanish nuances and cultural expectations. We adapt communication style to match your brand\'s tone for the Chilean market.' },
    ],
  }),

  // ═══════════════════════════════════════════════════════════
  // SUB-SAHARAN AFRICA
  // ═══════════════════════════════════════════════════════════

  ghana: makeCountry({
    slug: 'call-center-for-ghana',
    countryName: 'Ghana',
    countryFlag: '🇬🇭',
    timezone: 'GMT (GMT+0)',
    languages: ['English', 'French', 'Arabic'],
    currency: 'GHS / USD',
    title: 'Call Center Ghana 2026 | BPO Outsourcing Ghana – Call IT Dev',
    description: 'Call center outsourcing for Ghana. English & French agents, West African market expertise, GDPR-compliant. 50% savings. Free pilot!',
    keywords: ['call center Ghana', 'BPO Ghana', 'outsourcing Ghana', 'customer support Ghana', 'call center Accra', 'West Africa BPO'],
    heroTitle: 'Call Center for Ghana — West Africa\'s BPO Partner',
    heroSubtitle: 'Ghanaian companies: English & French agents, pan-African coverage, GDPR-compliant. 50% savings vs Accra. ISO 27001 certified.',
    whyTitle: 'Why Ghanaian Companies Choose Call IT Dev',
    whyIntro: 'Ghana\'s growing digital economy needs scalable, multilingual customer support. Morocco offers English, French, and Arabic — covering all of West Africa and beyond.',
    whyReasons: [
      { icon: '🗣️', title: 'English & French', description: 'Native speakers of both official West African languages.' },
      { icon: '⏰', title: 'Same Timezone', description: 'GMT+0/+1 — near-identical working hours with Accra.' },
      { icon: '💰', title: '50% Savings', description: 'Lower operational costs with superior infrastructure.' },
      { icon: '🌍', title: 'Pan-African Coverage', description: 'Serve Francophone and Anglophone Africa from one center.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'ISO 27001 — exceeding Ghana Data Protection Act requirements.' },
      { icon: '📱', title: 'Digital Channels', description: 'WhatsApp, social media, mobile money support integration.' },
    ],
    advantages: [
      { title: 'West Africa Hub', description: 'Cover Ghana, Nigeria, Senegal, Côte d\'Ivoire from one location.' },
      { title: 'Mobile Money Expertise', description: 'Support for mobile money and fintech services.' },
      { title: 'Telecom Experience', description: 'Proven track record with African telecom operators.' },
      { title: 'Scalable', description: 'From 5 to 200+ agents rapidly.' },
      { title: 'Quality Infrastructure', description: 'Tier-3 data centers, redundant connectivity.' },
      { title: 'Cultural Affinity', description: 'Shared African cultural understanding and business practices.' },
    ],
    pricingComparison: { local: '$5-10', morocco: '$6-12', savings: 'Premium quality' },
    industries: [
      { name: 'FinTech & Mobile Money', description: 'Customer support for Ghana\'s thriving mobile money ecosystem.' },
      { name: 'Telecom', description: 'Technical and billing support for telecom operators.' },
      { name: 'Mining & Oil', description: 'B2B support for Ghana\'s extractive industries.' },
      { name: 'E-commerce', description: 'Customer support for online marketplaces.' },
      { name: 'Banking', description: 'Customer service for banks and microfinance.' },
      { name: 'Agriculture', description: 'Support for agritech and export companies.' },
    ],
    faqs: [
      { question: 'Can you support mobile money platforms?', answer: 'Yes. We have extensive experience supporting mobile money, fintech, and digital payment platforms across Africa. Our agents handle transactions, disputes, and onboarding.' },
    ],
  }),

  senegal: makeCountry({
    slug: 'call-center-for-senegal',
    countryName: 'Senegal',
    countryFlag: '🇸🇳',
    timezone: 'GMT (GMT+0)',
    languages: ['French (native)', 'English', 'Arabic', 'Wolof'],
    currency: 'XOF / EUR',
    title: 'Centre d\'Appel Sénégal 2026 | BPO Outsourcing Sénégal – Call IT Dev',
    description: 'Centre d\'appel pour le Sénégal. Agents francophones natifs, couverture Afrique de l\'Ouest, conforme RGPD. 50% d\'économies vs Dakar.',
    keywords: ['centre appel Sénégal', 'BPO Sénégal', 'call center Senegal', 'externalisation Sénégal', 'centre d\'appel Dakar', 'outsourcing Afrique de l\'Ouest'],
    heroTitle: 'Centre d\'Appel pour le Sénégal — BPO Francophone Premium',
    heroSubtitle: 'Entreprises sénégalaises : agents francophones natifs, couverture UEMOA/CEDEAO, ISO 27001. 50% d\'économies vs Dakar.',
    whyTitle: 'Pourquoi les Entreprises Sénégalaises Choisissent Call IT Dev',
    whyIntro: 'Le Sénégal, hub numérique d\'Afrique de l\'Ouest, nécessite un support client francophone de qualité. Le Maroc offre la proximité culturelle et linguistique idéale.',
    whyReasons: [
      { icon: '🗣️', title: 'Francophone Natif', description: 'Agents francophones natifs pour une communication parfaite.' },
      { icon: '⏰', title: 'Même Fuseau', description: 'GMT+0/+1 — horaires quasi identiques avec Dakar.' },
      { icon: '🤝', title: 'Proximité Culturelle', description: 'Liens historiques Maroc-Sénégal, compréhension culturelle profonde.' },
      { icon: '💰', title: '50% Économies', description: 'Coûts inférieurs avec infrastructure supérieure.' },
      { icon: '🛡️', title: 'Conformité', description: 'ISO 27001, conforme aux réglementations UEMOA.' },
      { icon: '🌍', title: 'Couverture CEDEAO', description: 'Servez tout l\'espace francophone ouest-africain.' },
    ],
    advantages: [
      { title: 'Hub Francophone Africain', description: 'Couvrez Sénégal, Côte d\'Ivoire, Mali, Guinée, Cameroun depuis un centre.' },
      { title: 'Télécom & Mobile Money', description: 'Expertise dans le support des opérateurs télécoms et services financiers mobiles.' },
      { title: 'Arabe Natif', description: 'Agents arabophones pour le marché MENA en bonus.' },
      { title: 'Infrastructure', description: 'Data centers Tier-3, connectivité fibre redondante.' },
      { title: 'Scalable', description: 'De 5 à 300+ agents selon vos besoins.' },
      { title: 'Qualité', description: 'QA temps réel, suivi CSAT, formation continue.' },
    ],
    pricingComparison: { local: '$4-8 (XOF)', morocco: '$6-12', savings: 'Quality premium' },
    industries: [
      { name: 'Télécom', description: 'Support client pour les opérateurs télécoms sénégalais et ouest-africains.' },
      { name: 'Banque & Finance', description: 'Service client pour banques et institutions financières.' },
      { name: 'Mobile Money', description: 'Support pour les plateformes de paiement mobile.' },
      { name: 'E-commerce', description: 'Support client pour les marketplaces en ligne.' },
      { name: 'Immobilier', description: 'Gestion des appels pour le secteur immobilier en croissance.' },
      { name: 'Tourisme', description: 'Services de réservation et conciergerie multilingues.' },
    ],
    faqs: [
      { question: 'Parlez-vous wolof ?', answer: 'Nos agents communiquent principalement en français et anglais. Pour le wolof, nous travaillons avec des QA natifs pour valider la pertinence culturelle des interactions.' },
    ],
  }),

  south_africa: makeCountry({
    slug: 'call-center-for-south-africa',
    countryName: 'South Africa',
    countryFlag: '🇿🇦',
    timezone: 'SAST (GMT+2)',
    languages: ['English', 'French', 'Arabic', 'Afrikaans'],
    currency: 'ZAR / USD',
    title: 'Call Center South Africa 2026 | BPO Outsourcing South Africa – Call IT Dev',
    description: 'Call center outsourcing for South Africa. Multilingual agents, EU & MENA coverage, GDPR-compliant. Competitive rates. Free pilot!',
    keywords: ['call center South Africa', 'BPO South Africa', 'outsourcing South Africa', 'call center Johannesburg', 'customer support South Africa', 'Cape Town BPO'],
    heroTitle: 'Call Center for South Africa — Multilingual BPO from Morocco',
    heroSubtitle: 'South African companies: English + French + Arabic agents for pan-African and European markets. GMT+1 timezone, ISO 27001, GDPR-compliant.',
    whyTitle: 'Why South African Companies Choose Call IT Dev',
    whyIntro: 'South Africa\'s BPO sector is mature but expensive. Morocco offers complementary European and MENA languages at competitive rates with only 1h timezone difference.',
    whyReasons: [
      { icon: '🗣️', title: 'European Languages', description: 'French, German, Italian, Spanish — languages scarce in South Africa.' },
      { icon: '⏰', title: '1h Timezone Diff', description: 'GMT+1 vs GMT+2 — near-perfect working hour overlap.' },
      { icon: '🌍', title: 'North Africa Hub', description: 'Cover Francophone Africa and MENA from one center.' },
      { icon: '💰', title: 'Competitive Rates', description: 'Similar or lower costs with European language capabilities.' },
      { icon: '🛡️', title: 'GDPR & POPIA', description: 'Familiar with both EU and South African data protection laws.' },
      { icon: '📈', title: 'Scale Fast', description: 'Add 50-200 agents in weeks, not months.' },
    ],
    advantages: [
      { title: 'Language Complement', description: 'Add French, Arabic, German — languages SA lacks — to your support stack.' },
      { title: 'Pan-African Coverage', description: 'Serve Anglophone (you) + Francophone (us) Africa together.' },
      { title: 'EU Expansion', description: 'GDPR-ready support for European market entry.' },
      { title: 'Timezone Alignment', description: 'Only 1h difference — real-time collaboration.' },
      { title: 'Mining & Extractives', description: 'B2B support expertise for resource companies.' },
      { title: 'Quality Standards', description: 'ISO 27001, CI/CD for tech support, 95%+ CSAT.' },
    ],
    pricingComparison: { local: '$8-15 (ZAR)', morocco: '$6-12', savings: '30-50%' },
    industries: [
      { name: 'Mining & Resources', description: 'B2B support for South Africa\'s mining and resource sector.' },
      { name: 'Banking & Insurance', description: 'Customer service for financial institutions.' },
      { name: 'Telecom', description: 'Technical and billing support for SA telecom operators.' },
      { name: 'Retail & E-commerce', description: 'Multilingual customer support for retailers expanding into Africa.' },
      { name: 'Tourism', description: 'Booking and concierge services in multiple languages.' },
      { name: 'FinTech', description: 'Support for South Africa\'s growing fintech ecosystem.' },
    ],
    faqs: [
      { question: 'How do you complement South African BPO?', answer: 'We add European and MENA languages (French, Arabic, German, Italian) that are rare in SA, enabling pan-African and EU coverage alongside your existing English operations.' },
    ],
  }),

  // ═══════════════════════════════════════════════════════════
  // SOUTHEAST ASIA
  // ═══════════════════════════════════════════════════════════

  indonesia: makeCountry({
    slug: 'call-center-for-indonesia',
    countryName: 'Indonesia',
    countryFlag: '🇮🇩',
    timezone: 'WIB (GMT+7)',
    languages: ['English', 'French', 'Arabic', 'Indonesian'],
    currency: 'IDR / USD',
    title: 'Call Center Indonesia 2026 | BPO Outsourcing Indonesia – Call IT Dev',
    description: 'Call center outsourcing for Indonesia. European & MENA language support, GDPR-compliant. Follow-the-sun with 280M market. Free pilot!',
    keywords: ['call center Indonesia', 'BPO Indonesia', 'outsourcing Indonesia', 'customer support Jakarta', 'call center Jakarta', 'Indonesia BPO'],
    heroTitle: 'Call Center for Indonesia — EU & MENA Language Bridge',
    heroSubtitle: 'Indonesian companies going global: European languages (French, German, Spanish) + Arabic. Follow-the-sun with CET timezone. ISO 27001.',
    whyTitle: 'Why Indonesian Companies Choose Call IT Dev',
    whyIntro: 'Indonesia\'s 280M market is expanding internationally. Morocco provides the European and MENA language capabilities needed for global expansion.',
    whyReasons: [
      { icon: '🌍', title: 'EU & MENA Languages', description: 'Native French, German, Arabic, English for international expansion.' },
      { icon: '⏰', title: 'Follow-the-Sun', description: 'CET (GMT+1) complements WIB (GMT+7) for 24/7 coverage.' },
      { icon: '💰', title: 'Competitive', description: 'EU-grade support at nearshore rates.' },
      { icon: '🛡️', title: 'GDPR Ready', description: 'ISO 27001 — essential for EU market entry.' },
      { icon: '🗣️', title: '12+ Languages', description: 'Cover European and MENA markets from one center.' },
      { icon: '📈', title: 'Scale', description: 'From 10 to 500 agents for any language.' },
    ],
    advantages: [
      { title: 'European Expansion', description: 'Enter EU markets with native-language customer support.' },
      { title: 'MENA Bridge', description: 'Arabic capability for Middle Eastern expansion.' },
      { title: 'Follow-the-Sun', description: '6h offset for extended operating hours.' },
      { title: 'Compliance', description: 'GDPR-aligned, exceeding Indonesia\'s PDP Law.' },
      { title: 'E-commerce Expertise', description: 'Support for cross-border e-commerce platforms.' },
      { title: 'Quality', description: 'Real-time QA, CSAT tracking, continuous training.' },
    ],
    pricingComparison: { local: '$3-8', morocco: '$6-12', savings: 'EU premium' },
    industries: [
      { name: 'E-commerce', description: 'Multilingual support for Indonesian marketplaces going global.' },
      { name: 'FinTech', description: 'Customer support for Indonesian fintech companies.' },
      { name: 'Travel & Tourism', description: 'Multilingual booking and concierge services.' },
      { name: 'Telecom', description: 'Customer care for Indonesian telecom operators.' },
      { name: 'Ride-hailing & Logistics', description: 'Driver and customer support for mobility platforms.' },
      { name: 'SaaS & Tech', description: 'Technical support for Indonesian tech companies.' },
    ],
    faqs: [
      { question: 'How does Morocco complement Indonesian operations?', answer: 'We provide European and MENA languages (French, German, Arabic, Spanish, Italian) that are scarce in Indonesia, enabling 24/7 follow-the-sun support and international market coverage.' },
    ],
  }),

  singapore: makeCountry({
    slug: 'call-center-for-singapore',
    countryName: 'Singapore',
    countryFlag: '🇸🇬',
    timezone: 'SGT (GMT+8)',
    languages: ['English', 'French', 'Arabic', 'German'],
    currency: 'SGD / USD',
    title: 'Call Center Singapore 2026 | BPO Outsourcing Singapore – Call IT Dev',
    description: 'Call center outsourcing for Singapore. European & MENA languages, GDPR-compliant, ISO 27001. 50% savings for EU expansion. Free pilot!',
    keywords: ['call center Singapore', 'BPO Singapore', 'outsourcing Singapore', 'customer support Singapore', 'contact center Singapore'],
    heroTitle: 'Call Center for Singapore — Your EU & MENA Language Partner',
    heroSubtitle: 'Singaporean companies: add European languages (French, German, Spanish) + Arabic to your support stack. 50% savings, GDPR-compliant.',
    whyTitle: 'Why Singaporean Companies Choose Call IT Dev',
    whyIntro: 'Singapore\'s global businesses need European and MENA language coverage. Morocco delivers native-speaker support at a fraction of Singapore rates.',
    whyReasons: [
      { icon: '🗣️', title: 'European Languages', description: 'Native French, German, Spanish, Italian — rare in Singapore.' },
      { icon: '💰', title: '50% Savings', description: 'Significantly lower than Singapore-based multilingual support.' },
      { icon: '🛡️', title: 'GDPR & PDPA', description: 'Compliant with both EU and Singapore data protection.' },
      { icon: '⏰', title: 'Follow-the-Sun', description: '7h offset for extended EU-hours coverage.' },
      { icon: '🌍', title: 'MENA Access', description: 'Native Arabic for Middle Eastern market expansion.' },
      { icon: '🏆', title: 'Enterprise Grade', description: 'ISO 27001, SOC 2-aligned, tier-3 infrastructure.' },
    ],
    advantages: [
      { title: 'EU Market Access', description: 'Native European language support for Singapore\'s EU operations.' },
      { title: 'Cost Efficiency', description: '50%+ savings vs hiring multilingual agents in Singapore.' },
      { title: 'MENA Bridge', description: 'Arabic and French for Middle Eastern and North African markets.' },
      { title: 'Enterprise Security', description: 'ISO 27001, compliant with MAS regulations.' },
      { title: 'Timezone Complement', description: 'Cover European working hours while Singapore sleeps.' },
      { title: 'Scalable', description: 'Flexible team sizes from 5 to 200+ agents.' },
    ],
    pricingComparison: { local: '$15-30 (SGD)', morocco: '$6-12', savings: '50%+' },
    industries: [
      { name: 'FinTech & Banking', description: 'Multilingual support for Singapore\'s fintech hub.' },
      { name: 'Logistics & Shipping', description: 'Customer support for maritime and logistics companies.' },
      { name: 'SaaS & Tech', description: 'Technical support for APAC-based tech companies.' },
      { name: 'E-commerce', description: 'Cross-border retail customer support.' },
      { name: 'Gaming', description: 'Multilingual player support for game studios.' },
      { name: 'Consulting & Professional Services', description: 'Back-office and client support for consulting firms.' },
    ],
    faqs: [
      { question: 'Can you cover EU working hours for our Singapore company?', answer: 'Yes — our CET (GMT+1) timezone perfectly covers European business hours (9-18h CET) while your Singapore team handles APAC. This creates true 24/7 global coverage.' },
    ],
  }),

  // ═══════════════════════════════════════════════════════════
  // EASTERN EUROPE
  // ═══════════════════════════════════════════════════════════

  romania: makeCountry({
    slug: 'call-center-for-romania',
    countryName: 'Romania',
    countryFlag: '🇷🇴',
    timezone: 'EET (GMT+2)',
    languages: ['English', 'French', 'German', 'Arabic', 'Romanian'],
    currency: 'RON / EUR',
    title: 'Call Center Romania 2026 | BPO Outsourcing Romania – Call IT Dev',
    description: 'Call center outsourcing for Romania. French, German, Arabic agents at 40% savings vs Bucharest. ISO 27001, GDPR-compliant. Free pilot!',
    keywords: ['call center Romania', 'BPO Romania', 'outsourcing Romania', 'call center Bucharest', 'customer support Romania', 'nearshore Romania'],
    heroTitle: 'Call Center for Romania — Nearshore Alternative from Morocco',
    heroSubtitle: 'Romanian companies: French, German, Arabic + English agents. 40% savings vs Bucharest. Same CET/EET timezone, GDPR-compliant.',
    whyTitle: 'Why Romanian Companies Choose Call IT Dev',
    whyIntro: 'Romania\'s IT sector is booming but multilingual support costs are rising. Morocco offers French, Arabic, and German capabilities at competitive rates.',
    whyReasons: [
      { icon: '🗣️', title: 'French & Arabic', description: 'Native speakers of languages scarce in Romania.' },
      { icon: '⏰', title: '1h Timezone Diff', description: 'GMT+1 vs GMT+2 — near-identical working hours.' },
      { icon: '💰', title: '40% Savings', description: 'Lower costs than Bucharest with broader language coverage.' },
      { icon: '🛡️', title: 'EU Compliant', description: 'GDPR, ISO 27001 — same standards as Romania.' },
      { icon: '🌍', title: 'MENA Expertise', description: 'Arabic and French for markets Romania can\'t easily serve.' },
      { icon: '🚀', title: 'Fast Scaling', description: '2-week deployment for any language combination.' },
    ],
    advantages: [
      { title: 'Language Diversity', description: 'Add French, Arabic, German to Romania\'s English/Romanian capabilities.' },
      { title: 'MENA Markets', description: 'Unique Arabic capability for Middle Eastern expansion.' },
      { title: 'EU Standards', description: 'Same GDPR compliance, ISO certifications.' },
      { title: 'Cost Advantage', description: '40% savings while adding language diversity.' },
      { title: 'Timezone Proximity', description: 'Only 1h difference — seamless collaboration.' },
      { title: 'Infrastructure', description: 'Tier-3 data centers, redundant connectivity.' },
    ],
    pricingComparison: { local: '€8-15', morocco: '€6-10', savings: '40%+' },
    industries: [
      { name: 'IT & Software', description: 'Technical support for Romania\'s growing IT sector.' },
      { name: 'Automotive', description: 'Customer support for automotive manufacturers and suppliers.' },
      { name: 'Banking', description: 'Multilingual customer service for Romanian banks.' },
      { name: 'E-commerce', description: 'Customer support for online retailers.' },
      { name: 'Telecom', description: 'Technical and billing support.' },
      { name: 'BPO Services', description: 'White-label support for Romanian BPO companies.' },
    ],
    faqs: [
      { question: 'How do you compare to Romanian BPO providers?', answer: 'We complement Romanian BPOs by adding French, Arabic, and German — languages that are difficult to recruit for in Romania. Our rates are 40% lower with ISO 27001 and GDPR compliance.' },
    ],
  }),

  bulgaria: makeCountry({
    slug: 'call-center-for-bulgaria',
    countryName: 'Bulgaria',
    countryFlag: '🇧🇬',
    timezone: 'EET (GMT+2)',
    languages: ['English', 'French', 'German', 'Arabic'],
    currency: 'BGN / EUR',
    title: 'Call Center Bulgaria 2026 | BPO Outsourcing Bulgaria – Call IT Dev',
    description: 'Call center outsourcing for Bulgaria. French, German, Arabic agents at 35% savings vs Sofia. GDPR-compliant, ISO 27001.',
    keywords: ['call center Bulgaria', 'BPO Bulgaria', 'outsourcing Bulgaria', 'call center Sofia', 'customer support Bulgaria'],
    heroTitle: 'Call Center for Bulgaria — Multilingual Nearshore from Morocco',
    heroSubtitle: 'Bulgarian companies: add French, Arabic, German to your support stack. 35% savings vs Sofia. GDPR-compliant.',
    whyTitle: 'Why Bulgarian Companies Choose Call IT Dev',
    whyIntro: 'Bulgaria\'s EU membership drives demand for multilingual support. Morocco adds French, Arabic, and expanded European language coverage at competitive rates.',
    whyReasons: [
      { icon: '🗣️', title: 'French & Arabic', description: 'Languages unavailable in the Bulgarian talent pool.' },
      { icon: '⏰', title: '1h Timezone Diff', description: 'Near-identical working hours Sofia-Casablanca.' },
      { icon: '💰', title: '35% Savings', description: 'Competitive even vs Bulgaria\'s low costs.' },
      { icon: '🛡️', title: 'GDPR', description: 'Same EU compliance standards.' },
      { icon: '🌍', title: 'MENA Access', description: 'Native Arabic for Middle Eastern markets.' },
      { icon: '📈', title: 'Scalable', description: 'Rapid team assembly for any language.' },
    ],
    advantages: [
      { title: 'Language Expansion', description: 'French, Arabic, German — rare in Bulgaria.' },
      { title: 'EU Compliance', description: 'Same GDPR, ISO standards as Bulgaria.' },
      { title: 'Cost Competitive', description: 'Competitive rates even vs Bulgaria\'s low-cost market.' },
      { title: 'MENA Bridge', description: 'Arabic capability for regional expansion.' },
      { title: 'Timezone', description: '1h difference — real-time collaboration.' },
      { title: 'Quality', description: 'ISO 27001, continuous QA monitoring.' },
    ],
    pricingComparison: { local: '€6-12', morocco: '€6-10', savings: '20-35%' },
    industries: [
      { name: 'IT & Outsourcing', description: 'Support for Bulgaria\'s IT outsourcing sector.' },
      { name: 'Tourism', description: 'Multilingual booking and concierge services.' },
      { name: 'E-commerce', description: 'Customer support for online retailers.' },
      { name: 'Manufacturing', description: 'B2B support for Bulgarian manufacturers.' },
      { name: 'Real Estate', description: 'Support for international property buyers.' },
      { name: 'Energy', description: 'Customer service for energy companies.' },
    ],
    faqs: [
      { question: 'Is Morocco really cheaper than Bulgaria?', answer: 'For European languages like German and English, costs are similar. Where Morocco excels is adding French, Arabic, and Spanish at no premium — languages that are very expensive to recruit in Bulgaria.' },
    ],
  }),

  czech_republic: makeCountry({
    slug: 'call-center-for-czech-republic',
    countryName: 'Czech Republic',
    countryFlag: '🇨🇿',
    timezone: 'CET (GMT+1)',
    languages: ['English', 'French', 'German', 'Arabic'],
    currency: 'CZK / EUR',
    title: 'Call Center Czech Republic 2026 | BPO Outsourcing Czechia – Call IT Dev',
    description: 'Call center outsourcing for Czech Republic. French, German, Arabic agents at 45% savings vs Prague. Same CET timezone, GDPR-compliant.',
    keywords: ['call center Czech Republic', 'BPO Czech Republic', 'outsourcing Czechia', 'call center Prague', 'customer support Czech Republic'],
    heroTitle: 'Call Center for Czech Republic — Same CET, Premium Multilingual BPO',
    heroSubtitle: 'Czech companies: French, Arabic, German + English agents in the same CET timezone. 45% savings vs Prague. ISO 27001, GDPR.',
    whyTitle: 'Why Czech Companies Choose Call IT Dev',
    whyIntro: 'The Czech Republic\'s central European position demands multilingual support. Morocco provides French, Arabic, and expanded EU language coverage at significant savings vs Prague.',
    whyReasons: [
      { icon: '🗣️', title: 'French & Arabic', description: 'Native speakers for markets Czech companies want to enter.' },
      { icon: '⏰', title: 'Same CET', description: 'Identical timezone — real-time collaboration all day.' },
      { icon: '💰', title: '45% Savings', description: 'Lower costs than Prague for multilingual support.' },
      { icon: '🛡️', title: 'GDPR', description: 'Same EU compliance framework.' },
      { icon: '🌍', title: 'EU + MENA', description: 'Cover European and Middle Eastern markets.' },
      { icon: '🚀', title: 'Fast Launch', description: '2-3 week go-live for any language.' },
    ],
    advantages: [
      { title: 'CET Alignment', description: 'Same timezone as Prague — no scheduling issues.' },
      { title: 'Cost Advantage', description: '45% savings vs hiring multilingual agents in Prague.' },
      { title: 'French & Arabic', description: 'Natively available — extremely rare in Czech Republic.' },
      { title: 'Manufacturing Support', description: 'B2B support for Czech manufacturing export companies.' },
      { title: 'Automotive', description: 'Customer support for automotive sector.' },
      { title: 'EU Standards', description: 'ISO 27001, GDPR, enterprise-grade infrastructure.' },
    ],
    pricingComparison: { local: '€10-18', morocco: '€6-10', savings: '45%+' },
    industries: [
      { name: 'Automotive', description: 'Customer and dealer support for Czech automotive industry.' },
      { name: 'Manufacturing', description: 'B2B support for Czech manufacturers exporting to EU/MENA.' },
      { name: 'IT & Software', description: 'Technical support for Prague\'s tech companies.' },
      { name: 'Tourism', description: 'Multilingual tourist services and booking support.' },
      { name: 'E-commerce', description: 'Customer support for online retailers.' },
      { name: 'Energy', description: 'Customer service for energy providers.' },
    ],
    faqs: [
      { question: 'Can you serve Czech-speaking customers?', answer: 'Our core strength is adding French, Arabic, German, Spanish, and Italian to your support stack. For Czech language support, we recommend maintaining your local team while we handle international languages.' },
    ],
  }),

  hungary: makeCountry({
    slug: 'call-center-for-hungary',
    countryName: 'Hungary',
    countryFlag: '🇭🇺',
    timezone: 'CET (GMT+1)',
    languages: ['English', 'French', 'German', 'Arabic'],
    currency: 'HUF / EUR',
    title: 'Call Center Hungary 2026 | BPO Outsourcing Hungary – Call IT Dev',
    description: 'Call center outsourcing for Hungary. French, German, Arabic agents at 40% savings vs Budapest. Same CET timezone, GDPR-compliant.',
    keywords: ['call center Hungary', 'BPO Hungary', 'outsourcing Hungary', 'call center Budapest', 'customer support Hungary'],
    heroTitle: 'Call Center for Hungary — CET Nearshore BPO from Morocco',
    heroSubtitle: 'Hungarian companies: add French, Arabic, German to your multilingual stack. Same CET timezone, 40% savings vs Budapest. ISO 27001.',
    whyTitle: 'Why Hungarian Companies Choose Call IT Dev',
    whyIntro: 'Hungary\'s shared services sector needs diverse European and MENA languages. Morocco delivers native French, Arabic, and German at rates 40% below Budapest.',
    whyReasons: [
      { icon: '🗣️', title: 'French & Arabic', description: 'Native speakers for markets Hungary finds hard to serve.' },
      { icon: '⏰', title: 'Same CET', description: 'Identical working hours Budapest-Casablanca.' },
      { icon: '💰', title: '40% Savings', description: 'Lower than Budapest shared services centers.' },
      { icon: '🛡️', title: 'GDPR', description: 'EU-grade compliance identical to Hungary.' },
      { icon: '🌍', title: 'MENA Markets', description: 'Arabic capability for Middle Eastern expansion.' },
      { icon: '📊', title: 'Analytics', description: 'Real-time dashboards, CSAT, NPS tracking.' },
    ],
    advantages: [
      { title: 'SSC Alternative', description: 'More cost-effective than Budapest shared services for multilingual support.' },
      { title: 'French Native', description: 'Native French agents — not available at scale in Hungary.' },
      { title: 'Arabic Capability', description: 'Unique MENA language coverage.' },
      { title: 'Same Timezone', description: 'CET alignment — zero scheduling friction.' },
      { title: 'Automotive', description: 'Support for Hungary\'s automotive manufacturing sector.' },
      { title: 'EU Compliance', description: 'ISO 27001, GDPR — enterprise-ready.' },
    ],
    pricingComparison: { local: '€8-15', morocco: '€6-10', savings: '40%+' },
    industries: [
      { name: 'Automotive', description: 'Customer and supplier support for automotive companies.' },
      { name: 'Shared Services', description: 'White-label support for Budapest SSCs.' },
      { name: 'Pharma & Healthcare', description: 'Customer service for pharmaceutical companies.' },
      { name: 'IT & Telecom', description: 'Technical and billing support.' },
      { name: 'E-commerce', description: 'Multilingual customer support for online retailers.' },
      { name: 'Banking & Finance', description: 'Customer service for Hungarian financial institutions.' },
    ],
    faqs: [
      { question: 'How do you compare to Budapest SSCs?', answer: 'We offer 40% savings vs Budapest SSCs with additional French, Arabic, and Spanish capabilities. Same CET timezone, same GDPR compliance, but broader language coverage for international operations.' },
    ],
  }),
};
